import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, BadgeCheckIcon, CreditCardIcon, MessageCircleIcon, PhoneIcon } from 'lucide-react';
interface HeroProps {
  onCtaClick: () => void;
}
export function Hero({
  onCtaClick
}: HeroProps) {
  return <section className="relative min-h-screen w-full overflow-hidden bg-gradient-to-br from-brand-navy via-[#0d2a52] to-brand-deep" data-id="element-177">
      {/* Enhanced decorative elements with gold accents */}
      <div className="absolute inset-0 overflow-hidden" data-id="element-178">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-brand-primary/20 rounded-full blur-3xl" data-id="element-179" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-brand-aqua/20 rounded-full blur-3xl" data-id="element-180" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-gold/5 rounded-full blur-3xl" data-id="element-181" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-16 min-h-screen flex flex-col" data-id="element-182">
        {/* Premium Header Navigation */}
        <motion.nav initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="flex items-center justify-between py-6 mb-8" data-id="element-183">
          {/* Luxury Logo - Clean & Elegant */}
          <motion.div className="flex items-center gap-3" whileHover={{
          scale: 1.02
        }} transition={{
          duration: 0.2
        }} data-id="element-184">
            <div className="relative" data-id="element-185">
              <h2 className="text-4xl sm:text-5xl font-light tracking-[0.02em] text-white" data-id="element-186">
                DERMA
                <span className="font-serif italic text-brand-gold ml-1" data-id="element-187">
                  clinic
                </span>
              </h2>
              <div className="absolute -bottom-1 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-brand-gold to-transparent opacity-50" data-id="element-188" />
            </div>
          </motion.div>

          {/* Right side - Language + CTA */}
          <div className="flex items-center gap-3" data-id="element-189">
            {/* Language Selector - Minimal & Elegant */}
            <button className="hidden sm:flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-md rounded-full border border-white/10 text-white text-sm font-light transition-all duration-300 hover:bg-white/10 hover:border-brand-gold/30" data-id="element-190">
              <span className="text-base" data-id="element-191">🇺🇸</span>
              <span className="text-white/60" data-id="element-192">|</span>
              <span className="text-base" data-id="element-193">🇪🇸</span>
            </button>

            {/* Premium CTA Button */}
            <button onClick={onCtaClick} className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-brand-gold via-[#e0b456] to-brand-gold text-brand-navy font-semibold text-sm rounded-full shadow-xl shadow-brand-gold/40 transition-all duration-300 hover:shadow-2xl hover:shadow-brand-gold/60 hover:scale-105" data-id="element-194">
              <PhoneIcon className="w-4 h-4" data-id="element-195" />
              <span className="hidden sm:inline" data-id="element-196">Free Consultation</span>
              <span className="sm:hidden" data-id="element-197">Book Now</span>
            </button>
          </div>
        </motion.nav>

        {/* Main content grid - 2 columns on desktop */}
        <div className="flex-1 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-198">
          {/* Left column - Text content */}
          <div data-id="element-199">
            {/* Main headline with improved line-height */}
            <motion.h1 initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="text-4xl sm:text-5xl lg:text-5xl xl:text-6xl font-bold text-white leading-[1.15] mb-6" data-id="element-200">
              Transform Your Body With the{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-primary via-brand-gold to-brand-primary" data-id="element-201">
                Most Advanced
              </span>{' '}
              Aesthetic Treatments in the U.S.
            </motion.h1>

            {/* Subheadline */}
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.4
          }} className="text-lg sm:text-xl text-white/80 flex flex-wrap items-center gap-3 sm:gap-4 mb-10" data-id="element-202">
              <span className="flex items-center gap-2" data-id="element-203">
                <span className="w-1.5 h-1.5 bg-brand-gold rounded-full shadow-lg shadow-brand-gold/50" data-id="element-204" />
                <span className="font-light" data-id="element-205">Safe</span>
              </span>
              <span className="flex items-center gap-2" data-id="element-206">
                <span className="w-1.5 h-1.5 bg-brand-gold rounded-full shadow-lg shadow-brand-gold/50" data-id="element-207" />
                <span className="font-light" data-id="element-208">Certified Specialists</span>
              </span>
              <span className="flex items-center gap-2" data-id="element-209">
                <span className="w-1.5 h-1.5 bg-brand-gold rounded-full shadow-lg shadow-brand-gold/50" data-id="element-210" />
                <span className="font-light" data-id="element-211">10,000+ Happy Patients</span>
              </span>
            </motion.p>

            {/* CTA Button - Luxury Design */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.6
          }} className="mb-12" data-id="element-212">
              <button onClick={onCtaClick} className="group relative px-12 py-5 bg-gradient-to-r from-brand-gold via-[#e0b456] to-brand-gold text-brand-navy font-bold text-lg rounded-full overflow-hidden transition-all duration-300 shadow-2xl shadow-brand-gold/50 hover:shadow-brand-gold/70 hover:scale-105" aria-label="Claim your free consultation" data-id="element-213">
                <span className="relative z-10 flex items-center gap-3" data-id="element-214">
                  Claim Your Free Consultation
                  <span className="text-xl transition-transform duration-300 group-hover:translate-x-1" data-id="element-215">
                    →
                  </span>
                </span>
              </button>
            </motion.div>

            {/* Premium Trust badges - Minimal & Elegant */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.8
          }} className="flex flex-wrap gap-4" data-id="element-216">
              <div className="flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-md rounded-full border border-white/10" data-id="element-217">
                <ShieldCheckIcon className="w-4 h-4 text-brand-gold" data-id="element-218" />
                <span className="text-xs text-white/90 font-light" data-id="element-219">
                  FDA-Approved
                </span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-md rounded-full border border-white/10" data-id="element-220">
                <BadgeCheckIcon className="w-4 h-4 text-brand-gold" data-id="element-221" />
                <span className="text-xs text-white/90 font-light" data-id="element-222">
                  Board-Certified
                </span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-md rounded-full border border-white/10" data-id="element-223">
                <CreditCardIcon className="w-4 h-4 text-brand-gold" data-id="element-224" />
                <span className="text-xs text-white/90 font-light" data-id="element-225">
                  Financing Available
                </span>
              </div>
            </motion.div>
          </div>

          {/* Right column - Clinic image with luxury styling */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 1
        }} className="relative" data-id="element-226">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl ring-1 ring-brand-gold/20" data-id="element-227">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/b9rr6PQk9i7eBVBRDdWDaK/Screenshot_2025-12-03_at_9.23.41_PM.png" alt="Dermaclinic luxury reception area" className="w-full h-auto object-cover" data-id="element-228" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/60 via-transparent to-transparent" data-id="element-229" />

              {/* Elegant badge overlay */}
              <div className="absolute top-6 left-6 px-5 py-2 bg-brand-gold/95 backdrop-blur-sm rounded-full shadow-xl" data-id="element-230">
                <p className="text-brand-navy font-semibold text-sm tracking-wide" data-id="element-231">
                  Premium Facilities
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* WhatsApp Floating Button - Mobile Only */}
      <motion.a href="https://wa.me/1234567890" target="_blank" rel="noopener noreferrer" initial={{
      opacity: 0,
      scale: 0
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      delay: 2,
      duration: 0.5
    }} className="lg:hidden fixed bottom-6 right-6 z-50 w-16 h-16 bg-[#25D366] rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110 active:scale-95" aria-label="Contact us on WhatsApp" data-id="element-232">
        <MessageCircleIcon className="w-8 h-8 text-white" data-id="element-233" />
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-pulse" data-id="element-234" />
      </motion.a>
    </section>;
}